import { ScenarioExt } from '@/classes/scenario/ScenarioExt';
import _log from '@/helpers/log';
import { axiosWrapper } from '@/services/axiosWrapper';
import { Action, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import _t from '@/helpers/timeHelper';
import { ObjectOnly } from '@/classes/ObjectOnly';
import { download } from '@/helpers/download';
import { AxiosResponseHeaders } from 'axios';

interface Dependencies {
  get: <T>(url: string, params?: ObjectOnly) => Promise<{data: T, headers: AxiosResponseHeaders}>
  download: typeof download
  formatDate: (date: Date, locale: string) => string
}

interface LoadReportActionParams {
  withPVFilename?: string,
  withoutPVFilename?: string,
  locale: string
}

interface LoadExportAnalytics {
  year: string,
  month: string
}

interface ExecutivePDFSettings {
  day: string | number;
  month: string | number;
  currency: string;
  co2: string;
  energyCharts?: string;
}

export const buildReportModule = ({ get, download, formatDate }: Dependencies) => {
  function computeParams(paramsObject: Record<string, string | undefined>): string {
    return Object.entries(paramsObject)
      .filter(([_, value]) => value !== undefined)
      .map(([key, value]) => `${key}=${value}`)
      .join('&');
  }

  @Module({
    namespaced: true
  })
  class ReportModule extends VuexModule {
    private defaultReportSettings = {
      technicalSelection: 'withoutPV',
      gensetUsageSelection: 'withoutPV',
      ecoPVSelection: 'withoutPV',
      ecoCurrencySelection: 'usd',
      ecoCO2Selection: 'withoutCO2',
      cashFlowSelection: 'project',
      energyChartSelection: 'withoutPV',
      energyChartDay: 0,
      energyChartMonth: 0,
      energyCharts: new Array(2)
    };

    printPDF: boolean = false;

    data: null | any = null;
    executiveData: null | any = null;

    reportsSettings: any = {};
    currentUUID: string = '';
    workBookIsLoading: boolean = false;
    workBookIsLoadingRenFr: boolean = false;
    anlyticsExporting: boolean = false;
    // Actions
    @Action
    public async loadXLSXResults({ withPVFilename, withoutPVFilename, locale }: LoadReportActionParams): Promise<void> {
      try {
        const params = computeParams({ withPVFilename, withoutPVFilename });
        const { data } = await get<BlobPart>(`/algo/results/?${params}`, {
          headers: { 'Content-Type': 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet' },
          responseType: 'blob'
        });

        const scenarioName = this.context.getters.getConfig.name;
        const now = _t.getNow();
        download(`${formatDate(now, locale)}_${scenarioName}_result.xlsx`, data);
      } catch (error) {
        _log.logError(error);
      }
    }

    @Action
    public async loadWorkbook(workbookId: string): Promise<void> {
      try {
        this.context.commit('setWorkBookIsLoading', true);
        const { data, headers } = await get<BlobPart>(`/algo/workbook/${this.scenarioId}/${workbookId}`, {
          headers: { 'Content-Type': 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet' },
          responseType: 'blob'
        });

        const dispositionHeader = headers['content-disposition'];
        const matches = dispositionHeader ? dispositionHeader.match(/filename="(.+)"/) : null;

        download(matches && matches.length > 1 ? matches[1] : 'workbook.xlsm', data);
        this.context.commit('setWorkBookIsLoading', false);
      } catch (error) {
        _log.logError(error);
      }
    }

    @Action
    public async loadWorkbookRenFr(workbookId: string): Promise<void> {
      try {
        this.context.commit('setWorkBookIsLoadingRenFr', true);
        const { data, headers } = await get<BlobPart>(`/algo/workbookv2/${this.scenarioId}/${workbookId}`, {
          headers: { 'Content-Type': 'application/vnd.openxmlformatsofficedocument.spreadsheetml.sheet' },
          responseType: 'blob'
        });

        const dispositionHeader = headers['content-disposition'];
        const matches = dispositionHeader ? dispositionHeader.match(/filename="(.+)"/) : null;

        download(matches && matches.length > 1 ? matches[1] : 'workbook.xlsb', data);
        this.context.commit('setWorkBookIsLoadingRenFr', false);
      } catch (error) {
        _log.logError(error);
      }
    }

    @Action
    public async ExportAnalytics({ year, month }:LoadExportAnalytics): Promise<void> {
      try {
        this.context.commit('setAnlyticsExporting', true);
        const { data } = await get<BlobPart>(`/tools/analytics/export/${year}/${month}`, {
          headers: { 'Content-Type': 'application/zip' },
          responseType: 'blob'
        });

        // const matches = headers['content-disposition'].match(/filename="(.*)"/);
        download('analytics.zip', data);
        this.context.commit('setAnlyticsExporting', false);
      } catch (error) {
        _log.logError(error);
      }
    }

    @Action
    public getReportMockData() {
      return axiosWrapper
        .getFromLocalJson('report_mock')
        .then((res: any) => {
          this.context.commit('setReportData', res.data);
          return res.data;
        })
        .catch(_err => {
          _log.logError(_err);
          return false;
        });
    }

    @Action
    public getExecutiveReportDiagramData() {
      /* if (this.executiveData) {
        return Promise.resolve(true);
      } */
      const nameJson = this.context.getters.scenarioIsMulti ? 'diagram_executive_report_multi' : 'diagram_executive_report';
      return axiosWrapper
        .getFromLocalJson(nameJson)
        .then((res: any) => {
          this.context.commit('setExecutiveData', res.data);
          return res.data;
        })
        .catch(_err => {
          _log.logError(_err);
          return false;
        });
    }

    @Action
    public resetLocalReport() {
      this.context.commit('updateSettings', this.defaultReportSettings);
      this.context.commit('setReportData', null);
    }

    @Action
    public printExecutiveReport(print: boolean) {
      this.context.commit('updatePrintPDF', print);
    }

    @Action
    public updateReportSettings(payload: any) {
      this.context.commit('updateSettings', payload);
    }
    // end Actions

    @Mutation
    private setReportData(payload: any) {
      this.data = payload;
    }

    @Mutation
    private setWorkBookIsLoading(bool: boolean) {
      this.workBookIsLoading = bool;
    }

    @Mutation
    private setWorkBookIsLoadingRenFr(bool: boolean) {
      this.workBookIsLoadingRenFr = bool;
    }

    @Mutation
    private setUUID(payload: string) {
      this.currentUUID = payload;
    }

    @Mutation
    private setExecutiveData(payload: any) {
      this.executiveData = payload;
    }

    @Mutation
    private updateSettings(payload: any) {
      const uuid = payload && payload.uuid ? payload.uuid : this.scenarioId;
      if (uuid) {
        this.reportsSettings[uuid] = {
          ...this.reportsSettings[uuid],
          ...payload
        };
      }
      this.reportsSettings = { ...this.reportsSettings };
    }

    @Mutation
    private updatePrintPDF(print: any) {
      this.printPDF = print;
    }

    @Mutation
    private setAnlyticsExporting(bool: boolean) {
      this.anlyticsExporting = bool;
    }

    // end mutations

    get getDataIsLoading(): boolean {
      return this.anlyticsExporting;
    }

    get getConfig(): ScenarioExt {
      return new ScenarioExt(this.context.rootGetters['scenario/getFields']);
    }

    get scenarioId(): string | null {
      return this.currentUUID || this.getConfig.uuid;
    }

    get scenarioIsMulti(): boolean | null {
      return this.getConfig.hasGrid === false && this.getConfig.generator && this.getConfig.generator.isMulti;
    }

    get reportPreferences() {
      if (this.scenarioId) {
        this.reportsSettings[this.scenarioId] = {
          ...this.defaultReportSettings,
          ...this.reportsSettings[this.scenarioId]
        };
        return this.reportsSettings[this.scenarioId];
      }
      return this.defaultReportSettings;
    }

    // Technical
    get technicalSelection(): string {
      return this.reportPreferences.technicalSelection;
    }
    get gensetUsageSelection(): string {
      return this.reportPreferences.gensetUsageSelection;
    }

    get energyChartSelection(): string {
      return this.reportPreferences.energyChartSelection;
    }

    get energyChartDay(): number {
      return this.reportPreferences.energyChartDay;
    }

    get energyChartMonth(): number {
      return this.reportPreferences.energyChartMonth;
    }

    // Economical
    get ecoPVSelection(): string {
      return this.reportPreferences.ecoPVSelection;
    }

    get ecoCurrencySelection(): string {
      return this.reportPreferences.ecoCurrencySelection;
    }

    get ecoCO2Selection(): string {
      return this.reportPreferences.ecoCO2Selection;
    }
    get cashFlowSelection(): string {
      return this.reportPreferences.cashFlowSelection;
    }

    get reportData() {
      return this.data || this.context.rootGetters['scenario/getAlgoResult'];
    }

    get executiveDiagramData(): string {
      return this.executiveData;
    }

    get techReportData() {
      if (this.data) {
        return this.data.technical;
      }

      return this.context.rootGetters['scenario/getTechnicalResult'];
    }

    get ecoReportData() {
      if (this.data) {
        return this.data.eco;
      }

      return this.context.rootGetters['scenario/getEconomicalResult'];
    }

    get ecoReportRenFrData() {
      if (this.data) {
        return this.data;
      }

      return this.context.rootGetters['scenario/getEconomicalRenFrResult'];
    }

    get energyCharts() {
      return this.reportPreferences.energyCharts;
    }

    get parsedEnergyCharts() {
      const options = this.reportPreferences.energyCharts.filter(
        (item: any) => item
      );
      return JSON.stringify(options);
    }

    get executivePDFSettings(): ExecutivePDFSettings {
      return {
        currency: this.ecoCurrencySelection,
        co2: this.ecoCO2Selection,
        day: this.energyChartDay,
        month: this.energyChartMonth,
        energyCharts: this.parsedEnergyCharts
      };
    }

    get executivePrintPDF(): boolean {
      return this.printPDF;
    }

    get getWorkBookIsLoading(): boolean {
      return this.workBookIsLoading;
    }

    get getWorkBookIsLoadingRenFr(): boolean {
      return this.workBookIsLoadingRenFr;
    }
  }

  return ReportModule;
};
